import classNames from "classnames";
import { ReactNode, useId } from "react";
import { FieldError } from "react-hook-form";
import { useI18n } from "../../hooks/useTranslations";
import SvgQuestionMark from "../icons/SvgQuestionMark";
import styles from "./FormField.module.css";
import Icon from "./Icon";

interface Props {
  label?: string;
  children:
    | ReactNode
    | ((props: { labelId: string; isOptional: boolean; disabled: boolean; isInvalid: boolean }) => ReactNode);
  showHelp?: () => void;
  isOptional?: boolean;
  disabled?: boolean;
  error?: FieldError;
  hidden?: boolean;
  fullWidth?: boolean;
}

const FormField = ({
  label,
  showHelp,
  children,
  isOptional = false,
  disabled = false,
  error,
  hidden,
  fullWidth,
}: Props) => {
  const labelId = useId();
  const i18n = useI18n();
  const isInvalid = error !== undefined;

  return (
    <div
      className={classNames(styles.field, {
        [styles.disabled]: disabled,
        [styles.hidden]: hidden,
        [styles.fullWidth]: fullWidth,
      })}
    >
      {label && (
        <label htmlFor={labelId} className={styles.label}>
          {label}
          {isOptional && <span className={styles.optional}>({i18n.t.common.optional})</span>}
          {!!showHelp && <Icon glyph={SvgQuestionMark} className={styles.help} onClick={showHelp} />}
        </label>
      )}
      <div className={styles.data}>
        {typeof children === "function"
          ? children({
              labelId,
              isOptional,
              disabled,
              isInvalid,
            })
          : children}
        {isInvalid && <Error error={error} />}
      </div>
    </div>
  );
};

export const Error = ({ error }: { error: FieldError }) => <p className={styles.error}>{getErrorMessage(error)}</p>;

const getErrorMessage = ({ message, type }: FieldError): string => {
  const i18n = useI18n();

  if (message) return message;

  switch (type) {
    case "required":
      return i18n.t.common.fieldCannotBeEmpty;
    case "min":
      return i18n.t.common.fieldContainsInvalidValueInsertBiggerOne;
    case "max":
      return i18n.t.common.fieldContainsInvalidValueInsertSmallerOne;
    case "pattern":
      return i18n.t.common.fieldValueInvalidFormat;
    case "validate":
      return i18n.t.common.fieldInvalidValue;
    default:
      return "";
  }
};

FormField.Error = Error;

export default FormField;
